<template>
  <v-toolbar v-bind="$props" dense flat :color="color">
    <slot>
      <v-toolbar-title class="headline">
        {{ item.name }}
        <span></span>
        <offer-type-chip v-if="offerType" :value="offerType" class="mr-2" small />
        <span v-if="slug" class="caption">[{{ slug }}]</span>
      </v-toolbar-title>
    </slot>

    <v-spacer />

    <div
      v-if="publishChipVisible && !isArchived"
      class="d-flex align-center"
      :class="{ publishChipEditWrap: publishChipEditable }"
    >
      <transition :name="publishChip.transition">
        <div :key="publishChip.key" class="d-flex publish-chip">
          <v-tooltip v-if="chipIsToggled" max-width="200" bottom>
            <template #activator="{ on }">
              <v-icon class="mr-2" color="orange" v-on="on">info</v-icon>
            </template>
            <span>Click save for publishing changes to take effect!</span>
          </v-tooltip>
          <published-chip :value="publishChip.key" />
        </div>
      </transition>
      <toggle-input v-if="publishChipEditable" v-model="itemUpdated.published" label="" />
    </div>

    <div v-else-if="isArchived" class="d-flex align-center">
      <base-chip class="mr-4" icon="archive" text="Archived" />
      <toggle-input v-if="publishChipEditable" is-disabled label="" />
    </div>

    <v-menu
      v-if="menuVisible && menuOptions.length"
      :disabled="isLoading || hasPageErrors"
      transition="slide-y-transition"
      bottom
      close-on-content-click
      left
      lose-on-click
    >
      <template #activator="{ on }">
        <v-btn icon class="ml-2" v-on="on">
          <v-icon>more_vert</v-icon>
        </v-btn>
      </template>
      <v-list dense>
        <v-list-item v-for="(option, index) in menuOptions" :key="index" :to="option.to">
          <v-list-item-icon>
            <v-icon>{{ option.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ option.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-toolbar>
</template>

<script>
// Helpers
import { mapGetters } from 'vuex'
// Components
import { VToolbar } from 'vuetify/lib'
import OfferTypeChip from '@/components/chips/offer-type'
import PublishedChip from '@/components/chips/published'
import BaseChip from '@/components/chips/base-chip'
import ToggleInput from '@/components/forms/toggle-input'

export default {
  name: 'EditorToolbar',
  components: {
    OfferTypeChip,
    PublishedChip,
    ToggleInput,
    BaseChip,
  },
  extends: VToolbar,
  props: {
    color: {
      type: String,
      default: 'transparent',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isArchived: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    itemUpdated: {
      type: Object,
      default: () => ({}),
    },
    offerType: {
      type: String,
      default: undefined,
    },
    slug: {
      type: String,
      default: null,
    },
    menuVisible: {
      type: Boolean,
      default: true,
    },
    publishChipVisible: {
      type: Boolean,
      default: true,
    },
    publishChipEditable: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
      validator(val) {
        return ['partner', 'offer', 'link', 'deal', 'buy-guide'].includes(val)
      },
    },
  },
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    menuOptions() {
      return [
        {
          title: 'Manage Portals',
          icon: '$portals',
          show: ['partner'].includes(this.type),
          to: { name: 'data.portals-beta', query: { partner: this.slug } },
        },
        {
          title: 'Manage Reports',
          icon: '$reports',
          show: ['partner'].includes(this.type),
          to: { name: 'data.ledger-beta', query: { partner_slug: this.slug } },
        },
        {
          title: 'Manage Partner',
          icon: '$partners',
          show: !['offer', 'partner'].includes(this.type),
          to: { name: 'partners.edit', params: { slug: this.slug } },
        },
        {
          title: 'Manage Links',
          icon: '$links',
          show: !['link', 'offer'].includes(this.type),
          to: { name: 'links.edit', params: { slug: this.slug } },
        },
        {
          title: 'Manage Deals',
          icon: '$deals',
          show: !['offer', 'deal'].includes(this.type),
          to: { name: 'deals.management', query: { partner: this.slug } },
        },
      ].filter(({ show }) => show)
    },
    publishChip() {
      if (!this.publishChipEditable) return this.getPublishChipData(this.item.published)
      return this.getPublishChipData(this.itemUpdated.published)
    },
    chipIsToggled() {
      if (!Object.entries(this.itemUpdated).length) return false
      return this.item.published !== this.itemUpdated.published
    },
  },
  methods: {
    getPublishChipData(published) {
      return published ? { key: true, transition: 'toggle-publish' } : { key: false, transition: 'toggle-draft' }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/components/chip-toogle-animation.scss';

.publishChipEditWrap {
  position: relative;
  height: 100%;
}
.publishChipEditWrap .publish-chip {
  position: absolute;
  right: 65px;
}
</style>
